import classNames from 'classnames';
import {
  Button,
  Checkbox,
  DateInput,
  FlexContainer,
  InputFile,
  TitledBlock,
  Txt,
} from 'components';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useProperty, useDataActions } from 'state';
import readFile from 'helpers/readFile';
import styles from './TodoItemGAS.module.scss';

/**
 * TodoItemGAS
 *
 * @return {JSX.Element}
 */
const TodoItemGAS = ({ className, onSuccess }) => {
  const propertyDoc = useProperty();
  const storageActions = useDataActions();
  const [isLoading, setIsLoading] = useState(false);
  const [hasGasAppliances, setHasGasAppliances] = useState(
    () => propertyDoc.data.standards.hasGasAppliances
  );
  const [dateValue, setDateValue] = useState(
    () => propertyDoc.data.standards.gscExpiresOn?.toDate() || new Date()
  );
  const [fileValue, setFileValue] = useState();

  const handleFileChange = ({ target: { files } }) => {
    console.log('Files change', files);
    setFileValue(files[0]);
  };

  const handleDateChange = ({ target: { value } }) => {
    console.log('Date change', value);
    setDateValue(value);
  };

  const handleGasChange = ({ target: { checked } }) =>
    setHasGasAppliances(checked);

  const handleSubmit = async (e) => {
    console.log('Submit');
    e.preventDefault();
    setIsLoading(true);

    try {
      const date = new Date(dateValue);
      const documentBase64 = fileValue
        ? await readFile(fileValue)
        : 'data:application/pdf;base64,';

      await storageActions.property.updateGSC(
        propertyDoc,
        documentBase64,
        date,
        hasGasAppliances
      );

      return onSuccess && onSuccess();
    } catch (e) {
      setIsLoading(false);
      console.warn('Got error', e);
    }
  };

  const isDone = !!propertyDoc.data.standards.gscUrl;

  return (
    <FlexContainer
      className={classNames(
        styles.TodoItemGAS,
        className,
        TodoItemGAS.displayName
      )}
      column
      gap
    >
      <Txt>
        {isDone
          ? 'This property has a valid GSC  🎉'
          : '🔁 An GSC is valid for a year.'}
      </Txt>
      <Txt regular>A valid gas safety certificate is required by law.</Txt>
      <Checkbox
        checked={hasGasAppliances}
        onChange={handleGasChange}
        reverse
        className={styles.Checkbox}
      >
        <FlexContainer column gapHalf flex>
          <Txt>Does this property have gas appliances?</Txt>
          <Txt regular>
            If this property has gas appliances you are legally required to have
            a gas safety inspection carried out annually.
          </Txt>
        </FlexContainer>
      </Checkbox>
      <form onSubmit={handleSubmit}>
        <FlexContainer column gap>
          <FlexContainer
            column
            gap
            className={classNames(!hasGasAppliances && styles.Dim)}
          >
            <Txt regular>
              To present this certificate to your tenants please follow these
              steps:
            </Txt>
            <ol className={styles.List}>
              <li>Enter the date the inspection was carried out on.</li>
              <li>Upload the GSC using the button below.</li>
            </ol>
            <TitledBlock title="Inspection date">
              <FlexContainer column>
                <DateInput
                  type="date"
                  onChange={handleDateChange}
                  value={dateValue}
                  required={hasGasAppliances}
                  disabled={isLoading}
                  asInput
                  reverse
                  showYearDropdown
                />
              </FlexContainer>
            </TitledBlock>
            <InputFile
              onChange={handleFileChange}
              required={hasGasAppliances}
              disabled={isLoading}
              keepValue
              accept="application/pdf"
            >
              <Button flex blue>
                Upload a GSC
              </Button>
            </InputFile>
          </FlexContainer>
          <Button type="submit" success disabled={isLoading}>
            Save
          </Button>
        </FlexContainer>
      </form>
    </FlexContainer>
  );
};

TodoItemGAS.displayName = 'TodoItemGAS';

export default observer(TodoItemGAS);
